<script setup>
    import { computed } from 'vue'
    import { SelectItem, SelectItemIndicator, SelectItemText, useForwardProps } from 'radix-vue'
    import { Check, Lock } from 'lucide-vue-next'
    import { cn } from '@/lib/utils'
    import Tooltip from '@/components/Tooltip.vue'

    const props = defineProps({
        value: { type: null, required: true },
        disabled: { type: Boolean, required: false },
        locked: { type: Boolean, required: false },
        textValue: { type: String, required: false },
        asChild: { type: Boolean, required: false },
        as: { type: null, required: false },
        class: { type: null, required: false },
    })

    const delegatedProps = computed(() => {
        const { class: _, ...delegated } = props
        return delegated
    })

    const forwardedProps = useForwardProps(delegatedProps)
</script>

<template>
    <Tooltip v-if="props.locked">
        <template #trigger>
            <SelectItem
                v-bind="forwardedProps"
                :class="cn(
                    'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 focus:bg-accent focus:text-accent-foreground',
                    props.class
                )"
            >
                <span class="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                    <Lock class="h-4 w-4 text-muted-foreground" />
                </span>

                <SelectItemText>
                    <slot />
                </SelectItemText>
            </SelectItem>
        </template>
        <template #default>
            <div class="whitespace-nowrap text-xs">
                Unlock this option by upgrading your plan.
            </div>
        </template>
    </Tooltip>

    <SelectItem
        v-else
        v-bind="forwardedProps"
        :class="cn(
            'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 focus:bg-accent focus:text-accent-foreground',
            props.class
        )"
    >
        <span class="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
            <SelectItemIndicator>
                <Check class="h-4 w-4" />
            </SelectItemIndicator>
        </span>

        <SelectItemText>
            <slot />
        </SelectItemText>
    </SelectItem>
</template>
